import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX } from "react-icons/fi";
import { submitQuoteRequest } from "../api";

const inputClass = "w-full px-4 py-2 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";

export default function QuoteRequestModal({ serviceTitle, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      await submitQuoteRequest({ ...form, service: serviceTitle });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-navy border border-navy-border rounded-xl p-6 shadow-2xl relative"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <FiX size={20} />
          </button>

          {status === "success" ? (
            <div className="py-8 text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Thank you!</h3>
              <p className="text-gray-400">
                Your request for <span className="text-accent-light">{serviceTitle}</span> has been received.
                We'll get back to you soon.
              </p>
            </div>
          ) : (
            <>
              <h3 className="text-xl font-semibold text-white mb-1">Request Quote &amp; Information</h3>
              <p className="text-sm text-gray-400 mb-5">
                Interested in <span className="text-accent-light">{serviceTitle}</span>? Tell us a bit about your project.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  required
                  placeholder="Your Name"
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  required
                  type="email"
                  placeholder="Email Address"
                  className={inputClass}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <input
                  placeholder="Phone (optional)"
                  className={inputClass}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
                <textarea
                  placeholder="Tell us about your requirement..."
                  className={`${inputClass} min-h-[90px]`}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />

                {status === "error" && (
                  <p className="text-sm text-red-400">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full py-2.5 rounded-lg bg-brand-gradient text-white font-semibold shadow-md shadow-accent/20 disabled:opacity-60"
                >
                  {status === "submitting" ? "Sending..." : "Send Request"}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
